import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Switch, FormControlLabel, TextField, Box, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';

interface GameSettingsProps {
  open: boolean;
  onClose: (newSettings?: { highlightMoves: boolean; vintageMode: boolean; playerName: string }) => void;
  highlightMoves: boolean;
  vintageMode: boolean;
  playerName: string;
  isMobile?: boolean; // Added isMobile prop
  isLandscape?: boolean; // Added isLandscape prop
}

const GameSettings: React.FC<GameSettingsProps> = ({ 
  open, 
  onClose, 
  highlightMoves, 
  vintageMode,
  playerName,
  isMobile = false, // Default to false if not provided
  isLandscape = false // Default to false if not provided
}) => {
  const [localHighlightMoves, setLocalHighlightMoves] = useState(highlightMoves);
  const [localVintageMode, setLocalVintageMode] = useState(vintageMode);
  const [localPlayerName, setLocalPlayerName] = useState(playerName);

  // Update local state when props change (when dialog opens)
  useEffect(() => {
    if (open) {
      setLocalHighlightMoves(highlightMoves);
      // For mobile devices, always set to standard board mode
      setLocalVintageMode(isMobile ? false : vintageMode);
      setLocalPlayerName(playerName);
    }
  }, [open, highlightMoves, vintageMode, playerName, isMobile]);

  const handleSave = () => {
    const settings = {
      highlightMoves: localHighlightMoves,
      // Ensure mobile devices never save vintage board setting
      vintageMode: isMobile ? false : localVintageMode,
      playerName: localPlayerName.trim() || 'Player'
    };
    
    // Save settings to localStorage
    if (localPlayerName.trim()) {
      localStorage.setItem('captainsChess.playerName', localPlayerName.trim());
    }
    localStorage.setItem('captainsChess.useVintageBoard', String(isMobile ? false : localVintageMode));
    localStorage.setItem('captainsChess.highlightMoves', String(localHighlightMoves));
    
    onClose(settings);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog 
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backgroundImage: 'linear-gradient(rgba(39, 69, 115, 0.9), rgba(0, 0, 0, 0.9))',
          color: 'white',
          border: '1px solid rgba(255, 215, 0, 0.3)',
          boxShadow: '0 0 20px rgba(255, 215, 0, 0.2)',
          // Adjust height and overflow for mobile landscape mode
          ...(isLandscape && {
            maxHeight: '100%',
            height: 'auto',
            overflowY: 'visible'
          }),
          // Additional adjustments for mobile landscape mode
          ...(isLandscape && isMobile && {
            margin: '4px 8px', // Reduce margins
            maxHeight: '95vh', // Limit height
            width: '45%', // Make dialog 20% smaller for mobile landscape
            maxWidth: '45%'
          })
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        color: 'rgba(255, 215, 0, 0.8)', 
        borderBottom: '1px solid rgba(255, 215, 0, 0.3)',
        // Reduce padding for mobile landscape mode
        ...(isLandscape && isMobile && {
          padding: '6px',
          fontSize: '1.05rem',
          minHeight: '0',
          lineHeight: 1.2
        })
      }}>
        Player Settings
      </DialogTitle>
      
      <DialogContent sx={{ 
        // Prevent scrollbars in mobile landscape mode
        ...(isLandscape && {
          overflowY: 'visible',
          padding: '10px'
        }),
        ...(isLandscape && isMobile && {
          padding: '8px 10px',
          overflowY: 'auto', // Allow scrolling if needed
          maxHeight: 'calc(100vh - 90px)' // More restrictive height for mobile landscape
        })
      }}>
        <Box sx={{ 
          mt: isLandscape ? 0.5 : 3, 
          width: '100%', 
          maxWidth: 500, 
          mx: 'auto',
          // Compact spacing for landscape mode
          ...(isLandscape && {
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5
          }),
          // Even more compact for mobile landscape
          ...(isLandscape && isMobile && {
            gap: 0.25,
            mt: 0
          })
        }}>
          <TextField
            fullWidth
            label="Your Chess Name"
            variant="outlined"
            value={localPlayerName}
            onChange={(e) => setLocalPlayerName(e.target.value)}
            margin={isLandscape && isMobile ? "dense" : "normal"}
            inputProps={{ maxLength: 20 }}
            size={isLandscape && isMobile ? "small" : "medium"}
            sx={{ 
              mb: isLandscape ? (isMobile ? 0.5 : 1) : 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 215, 0, 0.5)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 215, 0, 0.8)',
                },
              },
              ...(isLandscape && isMobile && {
                '& .MuiInputLabel-root': {
                  fontSize: '0.9rem',
                  transform: 'translate(14px, 10px) scale(1)'
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.75)'
                }
              }),
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.8)',
              },
              '& .MuiOutlinedInput-input': {
                color: 'white',
                // Reduce input height for landscape mode
                ...(isLandscape && {
                  padding: '10px 14px'
                })
              }
            }}
          />
          
          <Box sx={{ mb: isLandscape ? (isMobile ? 1 : 1.5) : 2 }}>
            <Typography sx={{ 
              mb: isLandscape ? (isMobile ? 0.5 : 0.5) : 1, 
              color: 'rgba(255, 255, 255, 0.8)',
              ...(isLandscape && {
                fontSize: '0.9rem'
              }),
              ...(isLandscape && isMobile && {
                fontSize: '0.85rem'
              })
            }}>
              Board Style
            </Typography>
            {isMobile ? (
              // For mobile devices, only show standard board option
              <Box sx={{ 
                p: isLandscape && isMobile ? 0.75 : isLandscape ? 1 : 1.5, 
                border: '1px solid rgba(255, 215, 0, 0.3)', 
                borderRadius: '4px', 
                color: 'rgba(255, 255, 255, 0.8)',
                bgcolor: 'rgba(255, 215, 0, 0.3)'
              }}>
                <Typography sx={{
                  ...(isLandscape && {
                    fontSize: '0.9rem'
                  }),
                  ...(isLandscape && isMobile && {
                    fontSize: '0.8rem'
                  })
                }}>
                  Standard Board (Mobile View)
                </Typography>
                <Typography variant="caption" sx={{ 
                  display: 'block', 
                  mt: isLandscape && isMobile ? 0 : isLandscape ? 0.25 : 0.5, 
                  color: 'rgba(255, 255, 255, 0.6)',
                  ...(isLandscape && {
                    fontSize: '0.75rem'
                  }),
                  ...(isLandscape && isMobile && {
                    fontSize: '0.7rem'
                  })
                }}>
                  Vintage board is not available in mobile view
                </Typography>
              </Box>
            ) : (
              // For desktop, show toggle options
              <ToggleButtonGroup
                value={localVintageMode ? 'vintage' : 'standard'}
                exclusive
                onChange={(_, value) => {
                  if (value !== null) {
                    setLocalVintageMode(value === 'vintage');
                  }
                }}
                fullWidth
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'rgba(255, 255, 255, 0.8)',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255, 215, 0, 0.3)',
                      color: 'white',
                    },
                    ...(isLandscape && {
                      padding: '5px',
                      minHeight: '32px'
                    })
                  }
                }}
              >
                <ToggleButton value="standard">Standard</ToggleButton>
                <ToggleButton value="vintage">Vintage</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
          
          <FormControlLabel 
            control={
              <Switch
                checked={localHighlightMoves}
                onChange={() => setLocalHighlightMoves(!localHighlightMoves)}
                size={isLandscape && isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'rgba(255, 215, 0, 0.8)',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'rgba(255, 215, 0, 0.5)',
                  },
                  ...(isLandscape && {
                    padding: '4px'
                  }),
                  ...(isLandscape && isMobile && {
                    padding: '2px',
                    marginRight: '2px'
                  })
                }}
              />
            } 
            label="Highlight Moves"
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              ...(isLandscape && {
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.9rem'
                }
              }),
              ...(isLandscape && isMobile && {
                marginTop: '4px',  
                marginBottom: '2px',
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.8rem'
                }
              })
            }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        borderTop: '1px solid rgba(255, 215, 0, 0.3)', 
        padding: isLandscape && isMobile ? 1 : isLandscape ? 1 : 2, 
        justifyContent: 'center'
      }}>
        <Button 
          variant="contained" 
          onClick={handleSave}
          sx={{ 
            backgroundColor: 'rgba(255, 215, 0, 0.7)',
            color: 'black',
            '&:hover': {
              backgroundColor: 'rgba(255, 215, 0, 0.9)',
            },
            ...(isLandscape && {
              padding: '4px 10px',
              fontSize: '0.85rem'
            })
          }}
        >
          Save Settings
        </Button>
        <Button 
          variant="outlined" 
          onClick={handleCancel}
          sx={{ 
            ml: 1,
            borderColor: 'rgba(255, 255, 255, 0.5)',
            color: 'white',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.8)',
            },
            ...(isLandscape && {
              padding: '4px 10px',
              fontSize: '0.85rem'
            })
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GameSettings;
